import { useMutation, useQueryClient } from '@tanstack/react-query';

import { queryKeysCompanyAccount } from '~/data/company-account/queryKeys';

import { queryKeysCompany } from '../queryKeys';

import { moveCompany } from './moveCompany';
import {
  type MoveCompanyMutationOptions,
  type MoveCompanyVariables,
} from './types';

/**
 * Moves a company and invalidates company and company-account data afterward.
 *
 * @param options - Additional TanStack Query mutation options.
 * @returns The move-company mutation result.
 *
 * @see https://app.dev.vestigas.com/redoc#tag/VESTIGAS-Admin-Company/operation/move_company_vadmin_companies__company_id__move_post
 */
export function useMutationMoveCompany(options?: MoveCompanyMutationOptions) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (variables: MoveCompanyVariables) =>
      moveCompany(variables),
    ...options,
    async onSettled(data, error, variables, onMutateResult, context) {
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: [queryKeysCompany.base()],
        }),
        queryClient.invalidateQueries({
          queryKey: [queryKeysCompanyAccount.base()],
        }),
      ]);

      return options?.onSettled?.(
        data,
        error,
        variables,
        onMutateResult,
        context,
      );
    },
  });
}
